import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AccountStatisticsApiResponse,
  getUserAccountStatistics,
  LastReportDetailsApiResponse,
  LastReportDetailsResponse,
  getLastReportDetails,
  MetricsApiResponse,
  MetricsResponse,
  getUserAndSubscriptionMetrics,
  SiteEstimatorStats,
  FleetEstimatorStats,
} from "./dashboardAction";

interface DashboardStatistics {
  siteEstimator: SiteEstimatorStats;
  fleetEstimator: FleetEstimatorStats;
  planDuration: string;
  planExpiresIn: number | null;
}

interface DashboardState {
  statistics: DashboardStatistics | null;
  lastReport: LastReportDetailsResponse | null;
  metrics: MetricsResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: DashboardState = {
  statistics: null,
  lastReport: null,
  metrics: null,
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    resetDashboardState: (state) => {
      state.statistics = null;
      state.lastReport = null;
      state.metrics = null;
      state.error = null;
      state.loading = false;
    },
    updateSiteEstimatorUsage: (state, action: PayloadAction<{ used: number }>) => {
      if (state.statistics?.siteEstimator) {
        state.statistics.siteEstimator.totalReportsUsed = action.payload.used;
        state.statistics.siteEstimator.totalReportsRemaining = 
          state.statistics.siteEstimator.quotaLimit - action.payload.used;
      }
    },
    updateFleetEstimatorUsage: (state, action: PayloadAction<{ used: number }>) => {
      if (state.statistics?.fleetEstimator) {
        state.statistics.fleetEstimator.totalReportsUsed = action.payload.used;
        state.statistics.fleetEstimator.totalReportsRemaining = 
          state.statistics.fleetEstimator.quotaLimit - action.payload.used;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getUserAccountStatistics - UPDATED
      .addCase(getUserAccountStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserAccountStatistics.fulfilled,
        (state, action: PayloadAction<AccountStatisticsApiResponse>) => {
          state.loading = false;
          // The data now contains the statistics object directly
          state.statistics = action.payload.data?.statistics || null;
          state.error = null;
        }
      )
      .addCase(
        getUserAccountStatistics.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to retrieve account statistics";
        }
      )
      // Handle getLastReportDetails
      .addCase(getLastReportDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getLastReportDetails.fulfilled,
        (state, action: PayloadAction<LastReportDetailsApiResponse>) => {
          state.loading = false;
          state.lastReport = action.payload.data?.report || null;
          state.error = null;
        }
      )
      .addCase(
        getLastReportDetails.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to retrieve last report details";
        }
      )
      // Handle getUserAndSubscriptionMetrics
      .addCase(getUserAndSubscriptionMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getUserAndSubscriptionMetrics.fulfilled,
        (state, action: PayloadAction<MetricsApiResponse>) => {
          state.loading = false;
          state.metrics = action.payload.data || null;
          state.error = null;
        }
      )
      .addCase(
        getUserAndSubscriptionMetrics.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error =
            action.payload || "Failed to retrieve user and subscription metrics";
        }
      );
  },
});

export const { 
  resetDashboardState, 
  updateSiteEstimatorUsage, 
  updateFleetEstimatorUsage 
} = dashboardSlice.actions;
export default dashboardSlice.reducer;